





































































































































































import { Component, Mixins, Watch } from "vue-property-decorator";
import { namespace, State } from "vuex-class";
import MatrixSidebarMixin from "./MatrixSidebarMixin";
import { MatrixStoredFile } from "@/store/site-monitor-cameras/types";
import MatrixCameraMixin from "./MatrixCameraMixin";
import { AuditRecordDTO, EventRecord } from "@/store/site-monitor/types";
import { stringMixin } from "@/mixins";
import { AuditService } from "@/services/auditService";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import { AxiosRequestConfig } from "axios";

const { isNullOrWhitespace } = stringMixin.methods;

const SiteMonitor = namespace("siteMonitor");
const SiteMonitorCameras = namespace("siteMonitorCameras");

@Component({
	filters: {
		toMb: function(value: string) {
			const bytes = parseInt(value);
			return (bytes / 1024 / 1024).toFixed(2);
		}
	},
	mixins: [validationMixin],
	validations: {
		fileUploadNote: {
			required
		}
	}
})
export default class StoredFileList extends Mixins(MatrixSidebarMixin, MatrixCameraMixin) {
	$refs: {
		uploadFileInput: HTMLInputElement;
	};

	@State(State => State.User.permittedFileTypes) permittedFileTypesPref: string;

	@SiteMonitor.Getter("getAuditService") private auditService: AuditService;
	@SiteMonitor.Action private createAuditRecord: (payload: AuditRecordDTO) => Promise<EventRecord>;

	@SiteMonitorCameras.Getter("getDeviceControllerStoredFiles")
	private deviceControllerStoredFiles: MatrixStoredFile[];
	@SiteMonitorCameras.Mutation private setAwaitingStoredFile: (storedFile: MatrixStoredFile) => void;
	@SiteMonitorCameras.Mutation private clearDeviceControllerStoredFiles: () => void;
	@SiteMonitorCameras.Action private fetchStoredFiles: ({
		eventId,
		auditEndpoint,
		session
	}: {
		eventId: number;
		auditEndpoint: string;
		session: any;
	}) => Promise<void>;

	private fileSearchQuery: string = "";
	private fileListInterval: NodeJS.Timeout = null;
	private open = {
		video: false,
		image: false
	};

	private showFileUploadDialog: boolean = false;
	private fileUploadNote: string = "";
	private selectedFile: File = null;
	private uploadingFile: boolean = false;
	private uploadProgress: number = 0;
	private defaultPermittedFileTypes: string = "avi;mp4;mp3;pdf;png;jpeg;jpg;bmp;csv";

	/**
	 * Gets the list of stored files, optionally filtered by the users search query (if they've entered one).
	 */
	private get filteredFiles() {
		if (isNullOrWhitespace(this.fileSearchQuery)) {
			return this.deviceControllerStoredFiles;
		} else {
			return this.deviceControllerStoredFiles.filter(f =>
				f.fileName.toLowerCase().includes(this.fileSearchQuery.toLowerCase())
			);
		}
	}

	/**
	 * Gets just the stored video files.
	 */
	private get storedVideo() {
		return this.filteredFiles.filter(x => x.fileType == "vid");
	}

	/**
	 * Gets just the stored image files.
	 */
	private get storedImages() {
		return this.filteredFiles.filter(x => x.fileType == "img");
	}

	/**
	 * Returns the file icon (e.g. video or picture) for the currently selected file.
	 */
	private get selectedFileIcon() {
		if (this.selectedFile) {
			if (this.selectedFile.type.startsWith("image")) {
				return "fa-file-image";
			} else if (this.selectedFile.type.startsWith("video")) {
				return "fa-file-video";
			}
		}

		return "";
	}

	private get permittedFileTypes(): string {
		return this.permittedFileTypesPref ? this.permittedFileTypesPref : this.defaultPermittedFileTypes;
	}

	private get permittedFileTypesFormatted(): string {
		return `.${this.permittedFileTypes.replace(/;/g, ",.")}`;
	}

	/**
	 * BeforeDestroy hook, clears the file polling interval.
	 */
	private beforeDestroy() {
		clearInterval(this.fileListInterval);
	}

	/**
	 * Watches for when the event changes. When it does, we reset the component state and
	 * trigger an update for the stored file list.
	 */
	@Watch("eventDetails", { deep: true })
	private async onEventDetailsChanged(eventDetails) {
		this.fileSearchQuery = null;
		this.setAwaitingStoredFile(null);
		this.clearDeviceControllerStoredFiles();

		if (eventDetails) {
			await this.retrieveFileList();
			this.fileListInterval = setInterval(() => this.retrieveFileList(), 3000);
		} else {
			clearTimeout(this.fileListInterval);
		}
	}

	/**
	 * Handler for when the user changes the search query. If they enter a query, we
	 * expand the image/video sections automatically.
	 */
	@Watch("fileSearchQuery")
	private async onFileSearchQueryChanged(newValue) {
		if (!isNullOrWhitespace(newValue)) {
			this.open.video = true;
			this.open.image = true;
		}
	}

	/**
	 * Selects the file to be put into the Media Matrix.
	 */
	private selectFile(file: MatrixStoredFile) {
		if (this.isController) {
			this.setAwaitingStoredFile(file);
		}
	}

	/**
	 * Fetches the latest stored file list.
	 */
	private async retrieveFileList() {
		const auditSessionAuth = this.getSession(2);

		// If we're uploading a file, ensure we don't refresh the file list until it's complete,
		// as the file can appear in the list before audit has finished processing it.
		// Also skip updating the file list if we don't yet have an audit session.
		if (this.uploadingFile || !auditSessionAuth) {
			return;
		}

		try {
			await this.fetchStoredFiles({
				eventId: this.eventDetails.eventID,
				auditEndpoint: this.eventDetails.auditEndpoint,
				session: auditSessionAuth
			});
		} catch (err) {
			// Catches the failure so it doesn't hang as we call this method every 3 seconds
			console.error(`An error occurred while retrieving the list of stored files: ${err}`);
		}
	}

	/**
	 * Handles uploading a new video/image as a stored file. Checks to make sure the user
	 * has selected a valid file, and displays the upload file dialog to allow the user
	 * to add a note.
	 * @param file the file to upload.
	 */
	private uploadStoredFileClicked(file: File) {
		const fileTypeIsImageOrVideo = file.type.startsWith("image") || file.type.startsWith("video");
		const isPermittedExtension = this.permittedFileTypes.split(";").map((type) => type.trim())
		.includes(file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length));

		if (fileTypeIsImageOrVideo && isPermittedExtension) {
			this.selectedFile = file;

			if (!this.showFileUploadDialog) {
				this.showFileUploadDialog = true;
				this.fileUploadNote = "";
			}
		} else {
			this.$notify({
				title: "File Upload",
				type: "error",
				text: `Unable to upload ${file.name} as it's not an image or video file`
			});
		}
	}

	/**
	 * Creates an event record using the upload note, uploads the file to that
	 * event record, and audits the upload.
	 */
	private async uploadStoredFile() {
		if (this.$v.fileUploadNote.$invalid) {
			return;
		}

		this.uploadProgress = 0;
		this.uploadingFile = true;
		this.showFileUploadDialog = false;

		// Create an event record with the file upload note
		const { eventRecordID: eventRecordId } = await this.createAuditRecord({
			eventId: this.eventId,
			eventRecordTypeId: 103,
			details: this.fileUploadNote
		});

		/**
		 * Create a request config, with a handler for when axios raises an
		 * upload progress event. Calculates the total % uploaded, and
		 * updates the UI.
		 */
		const requestConfig: AxiosRequestConfig = {
			onUploadProgress: (ev: ProgressEvent) => {
				let percentComplete = Math.round((ev.loaded * 100) / ev.total);
				this.uploadProgress = percentComplete;
			}
		};

		const auth = this.getSession(2);
		const timeout = 300000;

		try {
			// Upload the file
			await this.auditService.uploadFileForEventRecord(
				"",
				this.selectedFile,
				auth,
				this.eventId,
				eventRecordId,
				timeout,
				requestConfig
			);

			// Notify the user
			this.$notify({
				title: "File Upload",
				type: "success",
				text: `${this.selectedFile.name} was uploaded successfully`
			});

			// Add a file upload audit record
			await this.createAuditRecord({
				eventId: this.eventId,
				eventRecordTypeId: 23,
				details: `File ${this.selectedFile.name} upload completed for EventRecordID: ${eventRecordId}`,
				objectId: eventRecordId
			});
		} catch (err) {
			this.$notify({
				title: "File Upload",
				type: "error",
				text: "Unable to upload file, please try again later or contact support if the problem persists"
			});

			console.error(`An error occurred while uploading file: ${err}`);
		}

		this.uploadingFile = false;

		// Update the file list
		await this.retrieveFileList();
	}
}
